import type { Schemas } from "@acervo/api-client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Empty, ErrorBox, PageHeader, Spinner } from "../../components/ui";
import { api } from "../../lib/api";
import { dateTime, relative } from "../../lib/format";

const route = getRouteApi("/_app/console/auditoria");
const PAGE = 50;

export const actionLabel: Record<string, string> = {
  "auth.login_succeeded": "Entrou",
  "auth.login_failed": "Falha de login",
  "auth.logout": "Saiu",
  "auth.mfa_enabled": "Ativou o 2FA",
  "auth.password_changed": "Trocou a senha",
  "auth.recovery_code_used": "Usou código de recuperação",
  "auth.refresh_reuse_detected": "Reuso de token (possível roubo)",
  "admin.owner_created": "Conta do dono criada",
  "admin.user_created": "Criou usuário",
  "admin.user_blocked": "Bloqueou usuário",
  "admin.user_unblocked": "Desbloqueou usuário",
  "admin.sessions_revoked": "Derrubou sessões",
  "admin.password_reset": "Gerou senha provisória",
  "admin.mfa_reset": "Removeu o 2FA",
  "admin.role_changed": "Mudou o papel",
  "admin.course_updated": "Editou curso",
  "admin.lesson_preview_set": "Alterou amostra grátis",
  "admin.media_imported": "Importou aulas",
};

const danger = new Set([
  "auth.login_failed",
  "auth.refresh_reuse_detected",
  "admin.user_blocked",
  "admin.mfa_reset",
]);

export function AuditPage() {
  const { action, target } = route.useSearch();
  const navigate = useNavigate();
  const q = useInfiniteQuery({
    queryKey: ["admin", "audit", action, target],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.unwrap(
        api.client.GET("/v1/admin/audit", {
          params: { query: { action, target, before: pageParam, limit: PAGE } },
        }),
      ),
    getNextPageParam: (last) =>
      last.items.length === PAGE ? last.items.at(-1)?.at : undefined,
  });

  return (
    <div>
      <PageHeader
        title="Auditoria"
        subtitle="Tudo que acontece com contas e acessos, do mais recente para o mais antigo."
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="f-action">
            Evento
          </label>
          <select
            id="f-action"
            className="field w-64"
            value={action ?? ""}
            onChange={(e) =>
              void navigate({
                to: "/console/auditoria",
                search: { action: e.target.value || undefined, target },
              })
            }
          >
            <option value="">Todos</option>
            {Object.entries(actionLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        {target && (
          <button
            className="btn-ghost"
            onClick={() =>
              void navigate({
                to: "/console/auditoria",
                search: { action, target: undefined },
              })
            }
          >
            Filtrando um alvo · limpar
          </button>
        )}
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <AuditTable items={q.data.pages.flatMap((p) => p.items)} />
          {q.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <button
                className="btn-ghost"
                disabled={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                {q.isFetchingNextPage ? "Carregando…" : "Carregar mais"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AuditTable({ items }: { items: Schemas["AuditEvent"][] }) {
  if (items.length === 0) return <Empty title="Nenhum evento" />;
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted uppercase">
          <tr className="border-b border-line">
            <th className="px-4 py-2 font-medium">Quando</th>
            <th className="px-4 py-2 font-medium">Evento</th>
            <th className="px-4 py-2 font-medium">Quem</th>
            <th className="px-4 py-2 font-medium">Alvo</th>
            <th className="px-4 py-2 font-medium">IP</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr
              key={e.id}
              className="border-b border-line/60 last:border-0 align-top"
            >
              <td
                className="px-4 py-2 whitespace-nowrap"
                title={dateTime(e.at)}
              >
                {relative(e.at)}
              </td>
              <td className="px-4 py-2">
                <span className={danger.has(e.action) ? "text-danger" : ""}>
                  {actionLabel[e.action] ?? e.action}
                </span>
                <Meta data={e.metadata} />
              </td>
              <td className="px-4 py-2">
                {e.actor_id ? (
                  <Link
                    to="/console/usuarios/$userId"
                    params={{ userId: e.actor_id }}
                    className="font-mono text-xs text-accent hover:underline"
                  >
                    {e.actor_id.slice(0, 8)}
                  </Link>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="px-4 py-2">
                {e.target_type === "user" && e.target_id ? (
                  <Link
                    to="/console/usuarios/$userId"
                    params={{ userId: e.target_id }}
                    className="font-mono text-xs text-accent hover:underline"
                  >
                    {e.target_id.slice(0, 8)}
                  </Link>
                ) : e.target_id ? (
                  <span className="font-mono text-xs text-muted">
                    {e.target_type}:{e.target_id.slice(0, 8)}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="px-4 py-2 font-mono text-xs text-muted">
                {e.ip ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Meta({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data ?? {}).filter(
    ([, v]) => v !== null && v !== "" && typeof v !== "object",
  );
  if (entries.length === 0) return null;
  return (
    <p className="mt-0.5 text-xs text-muted">
      {entries.map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
    </p>
  );
}
